import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { CatalogService } from "@/gen/omnios/v1/catalog_connect";
import type { SearchSynonym } from "@/gen/omnios/v1/catalog_pb";
import { PipelineService } from "@/gen/omnios/v1/pipeline_connect";
import { WorkspaceService } from "@/gen/omnios/v1/workspace_connect";
import { formatNumber } from "@/lib/format";
import { transport } from "@/lib/grpc";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { cn } from "@/lib/utils";
import { useWorkspaceStore } from "@/lib/workspace";
import { Struct } from "@bufbuild/protobuf";
import { createClient } from "@connectrpc/connect";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	Link,
	Outlet,
	createFileRoute,
	useLocation,
	useNavigate,
} from "@tanstack/react-router";
import {
	AlertCircle,
	Brain,
	Check,
	CheckCircle2,
	Download,
	FileUp,
	Loader2,
	Package,
	Plus,
	RefreshCw,
	Search,
	Settings,
	Sparkles,
	Trash2,
	Upload,
	Wand2,
	Zap,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const wsClient = createClient(WorkspaceService, transport);

export const Route = createFileRoute("/settings/catalog")({
	component: CatalogSettingsLayout,
});

const pipelineClient = createClient(PipelineService, transport);
const catalogClient = createClient(CatalogService, transport);

const AI_MODEL_VALUES = ["gpt-oss-120b/latest", "gpt-oss-20b/latest"] as const;
const AI_MODEL_KEYS: Record<string, string> = {
	"gpt-oss-120b/latest": "catalog_ai_model_large",
	"gpt-oss-20b/latest": "catalog_ai_model_fast",
};

const RESYNC_PIPELINE_NAME = "__typesense_resync";
const EMBEDDING_PIPELINE_NAME = "__embedding_sync";
const MATRIX_PIPELINE_NAME = "__enrichment_matrix";
const ENRICHMENT_PIPELINE_NAME = "__enrichment_batch";

async function upsertAndRun(
	name: string,
	description: string,
	operation: string,
) {
	let pipelineId: string;
	const existing = await pipelineClient.listPipelines({
		page: { pageSize: 100, pageToken: "" },
	});
	const found = existing.pipelines.find((p) => p.name === name);

	if (found) {
		pipelineId = found.id;
	} else {
		const created = await pipelineClient.createPipeline({
			name,
			description,
			// the server rejects a pipeline without a trigger ("Триггер обязателен"); these are button-started
			triggerEvent: "manual",
			steps: [
				{
					id: "run",
					operation,
					args: undefined,
					inputs: [],
					condition: undefined,
				},
			],
			isActive: true,
		});
		pipelineId = created.id;
	}

	const stream = pipelineClient.executePipeline({
		pipelineId,
		entrypointData: Struct.fromJson(
			{} as Parameters<typeof Struct.fromJson>[0],
		),
	});

	for await (const _step of stream) {
		// consume stream until done
	}
}

async function upsertAndRunWithArgs(
	name: string,
	description: string,
	operation: string,
	args: Record<string, unknown>,
) {
	let pipelineId: string;
	const existing = await pipelineClient.listPipelines({
		page: { pageSize: 100, pageToken: "" },
	});
	const found = existing.pipelines.find((p) => p.name === name);

	if (found) {
		pipelineId = found.id;
	} else {
		const created = await pipelineClient.createPipeline({
			name,
			description,
			triggerEvent: "manual",
			steps: [
				{
					id: "run",
					operation,
					args: Struct.fromJson(args as Parameters<typeof Struct.fromJson>[0]),
					inputs: [],
					condition: undefined,
				},
			],
			isActive: true,
		});
		pipelineId = created.id;
	}

	const stream = pipelineClient.executePipeline({
		pipelineId,
		entrypointData: Struct.fromJson(
			{} as Parameters<typeof Struct.fromJson>[0],
		),
	});

	for await (const _step of stream) {
		// consume stream until done
	}
}

const TAB_KEYS = [
	{ href: "/settings/catalog/search", key: "catalog_tab_search" },
	{ href: "/settings/catalog/enrichment", key: "catalog_tab_enrichment" },
	{ href: "/settings/catalog/synonyms", key: "catalog_tab_synonyms" },
	{ href: "/settings/catalog/migration", key: "catalog_tab_migration" },
];

function CatalogSettingsLayout() {
	const { t } = useTranslation("settings");
	useBreadcrumbs([
		{ label: t("breadcrumb_settings"), href: "/settings" },
		{ label: t("catalog_breadcrumb") },
	]);

	const qc = useQueryClient();

	const location = useLocation();
	const currentPath = location.pathname;
	const navigate = useNavigate();

	useEffect(() => {
		if (currentPath === "/settings/catalog") {
			navigate({ to: "/settings/catalog/search", replace: true });
		}
	}, [currentPath, navigate]);

	return (
		<div>
			<div className="flex items-center gap-2 px-4 py-2 shrink-0 justify-end">
				<Button
					variant="outline"
					size="sm"
					className="h-9"
					onClick={() =>
						qc.invalidateQueries({ queryKey: ["catalog-settings"] })
					}
				>
					{t("refresh", { defaultValue: "Обновить" })}
				</Button>
			</div>
			<nav className="-mx-6 -mt-4 flex border-b bg-muted/30">
				{TAB_KEYS.map((tab) => {
					const isActive = currentPath.startsWith(tab.href);

					return (
						<Link
							key={tab.href}
							to={tab.href}
							className={cn(
								"px-5 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors",
								isActive
									? "border-primary text-foreground bg-background"
									: "border-transparent text-muted-foreground hover:text-foreground",
							)}
						>
							{t(tab.key)}
						</Link>
					);
				})}
			</nav>

			<div className="pt-5">
				<Outlet />
			</div>
		</div>
	);
}

// ── Enrichment Tab ──────────────────────────

export function EnrichmentTab() {
	const { t } = useTranslation("settings");
	const [matrixDone, setMatrixDone] = useState(false);
	const [enrichDone, setEnrichDone] = useState(false);
	const [accurateDone, setAccurateDone] = useState(false);

	// Load enrichment-specific stats
	const { data: enrichStats } = useQuery({
		queryKey: ["enrichment-stats"],
		queryFn: () => catalogClient.getEnrichmentStats({}),
		refetchInterval: 15000,
	});

	const matrixMut = useMutation({
		mutationFn: () =>
			upsertAndRun(
				MATRIX_PIPELINE_NAME,
				"Построение матрицы обогащения товаров",
				"enrichment_matrix_rebuild",
			),
		onSuccess: () => {
			setMatrixDone(true);
			setTimeout(() => setMatrixDone(false), 3000);
		},
	});

	const enrichFastMut = useMutation({
		mutationFn: () =>
			upsertAndRunWithArgs(
				ENRICHMENT_PIPELINE_NAME,
				"Пакетное обогащение товаров",
				"enrichment_batch",
				{ mode: "fast", concurrency: 20 },
			),
		onSuccess: () => {
			setEnrichDone(true);
			setTimeout(() => setEnrichDone(false), 3000);
		},
	});

	const accurateMut = useMutation({
		mutationFn: () =>
			upsertAndRun(
				"__enrichment_accurate",
				"Точное обогащение товаров (LLM)",
				"enrichment_accurate_reprocess",
			),
		onSuccess: () => {
			setAccurateDone(true);
			setTimeout(() => setAccurateDone(false), 3000);
		},
	});

	return (
		<div className="space-y-4">
			{/* Stats dashboard */}
			{enrichStats && (
				<div className="space-y-4">
					{/* Counts row */}
					<div className="grid grid-cols-4 gap-3">
						<div className="rounded-lg border px-3 py-2">
							<div className="text-xs text-muted-foreground">
								{t("catalog_enrichment_total_products")}
							</div>
							<div className="text-lg font-semibold tabular-nums">
								{formatNumber(Number(enrichStats.totalProducts))}
							</div>
						</div>
						<div className="rounded-lg border px-3 py-2">
							<div className="text-xs text-muted-foreground">
								{t("catalog_enrichment_enriched")}
							</div>
							<div className="text-lg font-semibold tabular-nums">
								{formatNumber(Number(enrichStats.enrichedProducts))}
							</div>
						</div>
						<div className="rounded-lg border px-3 py-2">
							<div className="text-xs text-muted-foreground">
								{t("catalog_enrichment_pending_llm")}
							</div>
							<div className="text-lg font-semibold tabular-nums">
								{formatNumber(Number(enrichStats.pendingEnrichment))}
							</div>
						</div>
						<div className="rounded-lg border px-3 py-2">
							<div className="text-xs text-muted-foreground">
								{t("catalog_enrichment_avg_quality")}
							</div>
							<div className="text-lg font-semibold tabular-nums">
								{Math.round(enrichStats.avgQualityScore)}
							</div>
						</div>
					</div>

					{/* Quality distribution */}
					{Number(enrichStats.totalProducts) > 0 && (
						<div className="rounded-lg border px-3 py-3 space-y-2">
							<div className="text-xs text-muted-foreground font-medium">
								{t("catalog_enrichment_quality_distribution")}
							</div>
							<div className="flex items-end gap-1 h-12">
								{[
									{
										count: Number(enrichStats.quality76100),
										color: "bg-green-500",
										label: "76-100",
									},
									{
										count: Number(enrichStats.quality5175),
										color: "bg-amber-400",
										label: "51-75",
									},
									{
										count: Number(enrichStats.quality2650),
										color: "bg-orange-400",
										label: "26-50",
									},
									{
										count: Number(enrichStats.quality025),
										color: "bg-red-400",
										label: "0-25",
									},
								].map((bucket) => {
									const total = Number(enrichStats.totalProducts);
									const pct = total > 0 ? (bucket.count / total) * 100 : 0;
									return (
										<Tooltip key={bucket.label}>
											<TooltipTrigger asChild>
												<div
													className={cn(
														"flex-1 rounded-sm min-h-[2px]",
														bucket.color,
													)}
													style={{ height: `${Math.max(pct, 2)}%` }}
												/>
											</TooltipTrigger>
											<TooltipContent>
												{bucket.label}: {formatNumber(bucket.count)} (
												{Math.round(pct)}%)
											</TooltipContent>
										</Tooltip>
									);
								})}
							</div>
							<div className="flex justify-between text-[10px] text-muted-foreground/60">
								<span>76-100</span>
								<span>51-75</span>
								<span>26-50</span>
								<span>0-25</span>
							</div>
						</div>
					)}

					{/* Matrix stats */}
					{(enrichStats.titleRulesCount > 0 ||
						enrichStats.brandAliasesCount > 0 ||
						enrichStats.categoryCentroidsCount > 0) && (
						<div className="flex items-center gap-4 text-xs text-muted-foreground">
							<span>
								{t("catalog_enrichment_extraction_rules")}{" "}
								<span className="font-medium text-foreground">
									{enrichStats.titleRulesCount}
								</span>
							</span>
							<span>
								{t("catalog_enrichment_brands")}{" "}
								<span className="font-medium text-foreground">
									{enrichStats.brandAliasesCount}
								</span>
							</span>
							<span>
								{t("catalog_enrichment_centroids")}{" "}
								<span className="font-medium text-foreground">
									{enrichStats.categoryCentroidsCount}
								</span>
							</span>
							<span>
								{t("catalog_enrichment_patterns")}{" "}
								<span className="font-medium text-foreground">
									{enrichStats.categoryPatternsCount}
								</span>
							</span>
						</div>
					)}
				</div>
			)}

			{/* Matrix rebuild */}
			<Card className="px-4 py-4 space-y-4">
				<div className="flex items-start gap-3">
					<div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-50 text-amber-600 dark:bg-amber-950/30 dark:text-amber-400">
						<Brain className="h-4.5 w-4.5" />
					</div>
					<div className="flex-1 min-w-0">
						<h3 className="text-sm font-medium">
							{t("catalog_enrichment_matrix_title")}
						</h3>
						<p className="text-xs text-muted-foreground mt-0.5">
							{t("catalog_enrichment_matrix_desc")}
						</p>
					</div>
				</div>
				<div className="flex items-center gap-3 pt-1">
					<Button
						variant="outline"
						size="sm"
						disabled={matrixMut.isPending}
						onClick={() => matrixMut.mutate()}
					>
						{matrixMut.isPending ? (
							<>
								<Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
								{t("catalog_enrichment_matrix_building")}
							</>
						) : matrixDone ? (
							<>
								<Check className="h-3.5 w-3.5 mr-1.5 text-green-600" />
								{t("catalog_enrichment_matrix_done")}
							</>
						) : (
							<>
								<RefreshCw className="h-3.5 w-3.5 mr-1.5" />
								{t("catalog_enrichment_matrix_rebuild")}
							</>
						)}
					</Button>
				</div>
				{matrixMut.isError && (
					<p className="text-sm text-destructive">
						{matrixMut.error instanceof Error
							? matrixMut.error.message
							: t("catalog_enrichment_matrix_error")}
					</p>
				)}
			</Card>

			{/* Fast enrichment */}
			<Card className="px-4 py-4 space-y-4">
				<div className="flex items-start gap-3">
					<div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-green-50 text-green-600 dark:bg-green-950/30 dark:text-green-400">
						<Zap className="h-4.5 w-4.5" />
					</div>
					<div className="flex-1 min-w-0">
						<h3 className="text-sm font-medium">
							{t("catalog_enrichment_fast_title")}
						</h3>
						<p className="text-xs text-muted-foreground mt-0.5">
							{t("catalog_enrichment_fast_desc")}
						</p>
					</div>
				</div>
				<div className="flex items-center gap-3 pt-1">
					<Button
						variant="outline"
						size="sm"
						disabled={enrichFastMut.isPending}
						onClick={() => enrichFastMut.mutate()}
					>
						{enrichFastMut.isPending ? (
							<>
								<Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
								{t("catalog_enrichment_fast_running")}
							</>
						) : enrichDone ? (
							<>
								<Check className="h-3.5 w-3.5 mr-1.5 text-green-600" />
								{t("catalog_enrichment_fast_done")}
							</>
						) : (
							<>
								<Zap className="h-3.5 w-3.5 mr-1.5" />
								{t("catalog_enrichment_fast_start")}
							</>
						)}
					</Button>
				</div>
				{enrichFastMut.isError && (
					<p className="text-sm text-destructive">
						{enrichFastMut.error instanceof Error
							? enrichFastMut.error.message
							: t("catalog_enrichment_fast_error")}
					</p>
				)}
			</Card>

			{/* Accurate enrichment */}
			<Card className="px-4 py-4 space-y-4">
				<div className="flex items-start gap-3">
					<div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-purple-50 text-purple-600 dark:bg-purple-950/30 dark:text-purple-400">
						<Wand2 className="h-4.5 w-4.5" />
					</div>
					<div className="flex-1 min-w-0">
						<h3 className="text-sm font-medium">
							{t("catalog_enrichment_accurate_title")}
						</h3>
						<p className="text-xs text-muted-foreground mt-0.5">
							{t("catalog_enrichment_accurate_desc")}
						</p>
					</div>
				</div>
				<div className="flex items-center gap-3 pt-1">
					<Button
						variant="outline"
						size="sm"
						disabled={accurateMut.isPending}
						onClick={() => accurateMut.mutate()}
					>
						{accurateMut.isPending ? (
							<>
								<Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
								{t("catalog_enrichment_accurate_running")}
							</>
						) : accurateDone ? (
							<>
								<Check className="h-3.5 w-3.5 mr-1.5 text-green-600" />
								{t("catalog_enrichment_accurate_done")}
							</>
						) : (
							<>
								<Wand2 className="h-3.5 w-3.5 mr-1.5" />
								{t("catalog_enrichment_accurate_start")}
							</>
						)}
					</Button>
				</div>
				{accurateMut.isError && (
					<p className="text-sm text-destructive">
						{accurateMut.error instanceof Error
							? accurateMut.error.message
							: t("catalog_enrichment_accurate_error")}
					</p>
				)}
			</Card>
		</div>
	);
}

// ── Index Tab ────────────────────────────────

export function SearchTab() {
	const { t } = useTranslation("settings");
	const qc = useQueryClient();
	const [resyncDone, setResyncDone] = useState(false);
	const [embeddingDone, setEmbeddingDone] = useState(false);
	const { workspaces, selectedId, fetchWorkspaces } = useWorkspaceStore();
	const currentWs = workspaces.find((w) => w.id === selectedId);

	const { data: stats } = useQuery({
		queryKey: ["search-index-stats"],
		queryFn: () => catalogClient.getSearchIndexStats({}),
		refetchInterval: 30000,
	});

	const { data: settings, isLoading } = useQuery({
		queryKey: ["search-settings"],
		queryFn: () => catalogClient.getSearchSettings({}),
	});

	const [numTypos, setNumTypos] = useState<number | null>(null);
	const [stemmingEnabled, setStemmingEnabled] = useState<boolean | null>(null);
	const [stemmingLocale, setStemmingLocale] = useState<string | null>(null);
	const [aiModel, setAiModel] = useState<string | null>(null);

	const actualNumTypos = numTypos ?? settings?.numTypos ?? 2;
	const actualStemmingEnabled =
		stemmingEnabled ?? settings?.stemmingEnabled ?? true;
	const actualStemmingLocale =
		stemmingLocale ?? settings?.stemmingLocale ?? "ru";
	const actualAiModel =
		aiModel ?? currentWs?.settings?.ai_model ?? "gpt-oss-120b/latest";

	const searchDirty =
		numTypos !== null || stemmingEnabled !== null || stemmingLocale !== null;
	const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const [searchSaving, setSearchSaving] = useState(false);
	const [searchSaved, setSearchSaved] = useState(false);

	useEffect(() => {
		if (!searchDirty) return;
		if (searchTimer.current) clearTimeout(searchTimer.current);
		searchTimer.current = setTimeout(async () => {
			setSearchSaving(true);
			setSearchSaved(false);
			try {
				await catalogClient.updateSearchSettings({
					numTypos: actualNumTypos,
					stemmingEnabled: actualStemmingEnabled,
					stemmingLocale: actualStemmingLocale,
				});
				qc.invalidateQueries({ queryKey: ["search-settings"] });
				setSearchSaved(true);
				setTimeout(() => setSearchSaved(false), 2000);
			} catch {
				/* */
			} finally {
				setSearchSaving(false);
			}
		}, 800);
		return () => {
			if (searchTimer.current) clearTimeout(searchTimer.current);
		};
	}, [
		searchDirty,
		actualNumTypos,
		actualStemmingEnabled,
		actualStemmingLocale,
		qc,
	]);

	const aiModelMut = useMutation({
		mutationFn: async (model: string) => {
			if (!selectedId) return;
			const merged = { ...(currentWs?.settings ?? {}), ai_model: model };
			await wsClient.updateWorkspace({
				id: selectedId,
				settings: Struct.fromJson(
					merged as Parameters<typeof Struct.fromJson>[0],
				),
			});
		},
		onSuccess: () => {
			fetchWorkspaces();
		},
	});

	const resyncMut = useMutation({
		mutationFn: () =>
			upsertAndRun(
				RESYNC_PIPELINE_NAME,
				"Синхронизация каталога",
				"typesense_resync",
			),
		onSuccess: () => {
			setResyncDone(true);
			setTimeout(() => setResyncDone(false), 3000);
		},
	});

	const embeddingMut = useMutation({
		mutationFn: () =>
			upsertAndRun(
				EMBEDDING_PIPELINE_NAME,
				"Генерация эмбеддингов",
				"embedding_sync",
			),
		onSuccess: () => {
			setEmbeddingDone(true);
			setTimeout(() => setEmbeddingDone(false), 3000);
		},
	});

	return (
		<div className="space-y-5">
			{/* Stats row */}
			{stats && (
				<div className="flex items-center gap-6 text-sm">
					<div>
						<span className="text-muted-foreground">
							{t("catalog_search_in_index")}
						</span>
						<span className="font-medium tabular-nums">
							{formatNumber(Number(stats.totalIndexed))}
						</span>
					</div>
					<div>
						<span className="text-muted-foreground">
							{t("catalog_search_embeddings")}
						</span>
						<span className="font-medium tabular-nums">
							{formatNumber(Number(stats.withEmbeddings))}
						</span>
						{Number(stats.totalIndexed) > 0 && (
							<span className="text-xs text-muted-foreground ml-1">
								(
								{Math.round(
									(Number(stats.withEmbeddings) / Number(stats.totalIndexed)) *
										100,
								)}
								%)
							</span>
						)}
					</div>
					<div className="flex items-center gap-2">
						<Button
							variant="outline"
							size="sm"
							disabled={resyncMut.isPending}
							onClick={() => resyncMut.mutate()}
						>
							{resyncMut.isPending ? (
								<>
									<Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
									{t("catalog_search_syncing")}
								</>
							) : resyncDone ? (
								<>
									<Check className="h-3.5 w-3.5 mr-1.5 text-green-600" />
									{t("catalog_search_done")}
								</>
							) : (
								<>
									<RefreshCw className="h-3.5 w-3.5 mr-1.5" />
									{t("catalog_search_resync")}
								</>
							)}
						</Button>
						<Button
							variant="outline"
							size="sm"
							disabled={embeddingMut.isPending}
							onClick={() => embeddingMut.mutate()}
						>
							{embeddingMut.isPending ? (
								<>
									<Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
									{t("catalog_search_embeddings_running")}
								</>
							) : embeddingDone ? (
								<>
									<Check className="h-3.5 w-3.5 mr-1.5 text-green-600" />
									{t("catalog_search_done")}
								</>
							) : (
								<>
									<Sparkles className="h-3.5 w-3.5 mr-1.5" />
									{t("catalog_search_embeddings_update")}
								</>
							)}
						</Button>
					</div>
				</div>
			)}

			{/* Search settings */}
			{!isLoading && (
				<div className="grid gap-4 lg:grid-cols-3">
					<Card className="p-4 space-y-3">
						<div className="text-sm font-medium">
							{t("catalog_search_typos")}
						</div>
						<div className="flex items-center gap-2">
							{[0, 1, 2].map((n) => (
								<Button
									key={n}
									variant="outline"
									size="sm"
									onClick={() => setNumTypos(n)}
									className={cn(
										"w-10 h-9",
										actualNumTypos === n
											? "border-primary bg-primary/5 text-primary"
											: "border-border hover:border-primary/50",
									)}
								>
									{n}
								</Button>
							))}
						</div>
					</Card>

					<Card className="p-4 space-y-3">
						<div className="flex items-center justify-between">
							<div className="text-sm font-medium">
								{t("catalog_search_stemming")}
							</div>
							<Switch
								checked={actualStemmingEnabled}
								onCheckedChange={setStemmingEnabled}
							/>
						</div>
						{actualStemmingEnabled && (
							<Select
								value={actualStemmingLocale}
								onValueChange={setStemmingLocale}
							>
								<SelectTrigger className="w-full h-9">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="ru">
										{t("catalog_search_stemming_ru")}
									</SelectItem>
									<SelectItem value="en">
										{t("catalog_search_stemming_en")}
									</SelectItem>
									<SelectItem value="de">
										{t("catalog_search_stemming_de")}
									</SelectItem>
									<SelectItem value="fr">
										{t("catalog_search_stemming_fr")}
									</SelectItem>
								</SelectContent>
							</Select>
						)}
					</Card>

					<Card className="p-4 space-y-3">
						<div className="text-sm font-medium">
							{t("catalog_search_ai_model")}
						</div>
						<Select
							value={actualAiModel}
							onValueChange={(v) => {
								setAiModel(v);
								aiModelMut.mutate(v);
							}}
						>
							<SelectTrigger className="w-full h-9">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{AI_MODEL_VALUES.map((v) => (
									<SelectItem key={v} value={v}>
										{t(AI_MODEL_KEYS[v])}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						{aiModelMut.isPending && (
							<span className="flex items-center gap-1.5 text-xs text-muted-foreground">
								<Loader2 className="h-3 w-3 animate-spin" />
								{t("catalog_search_saving")}
							</span>
						)}
					</Card>
				</div>
			)}

			{/* Autosave indicator */}
			<div className="h-4">
				{searchSaving && (
					<span className="flex items-center gap-1.5 text-xs text-muted-foreground">
						<Loader2 className="h-3 w-3 animate-spin" />
						{t("catalog_search_saving")}
					</span>
				)}
				{searchSaved && !searchSaving && (
					<span className="flex items-center gap-1.5 text-xs text-green-600">
						<Check className="h-3 w-3" />
						{t("catalog_search_saved")}
					</span>
				)}
			</div>
		</div>
	);
}

// ── Migration Tab ─────────────────────────────

export function MigrationTab() {
	const { t } = useTranslation("settings");
	const [includeEmbeddings, setIncludeEmbeddings] = useState(true);
	const [includeEnrichment, setIncludeEnrichment] = useState(true);
	const [includeCompliance, setIncludeCompliance] = useState(true);
	const [importFile, setImportFile] = useState<File | null>(null);
	const [overwrite, setOverwrite] = useState(true);
	const fileInputRef = useRef<HTMLInputElement>(null);

	const exportMut = useMutation({
		mutationFn: async () => {
			const result = await catalogClient.exportCatalog({
				includeEmbeddings,
				includeEnrichmentState: includeEnrichment,
				includeCompliance,
			});

			// Download the file
			if (result.downloadUrl) {
				const response = await fetch(result.downloadUrl);
				const blob = await response.blob();
				const url = URL.createObjectURL(blob);
				const a = document.createElement("a");
				a.href = url;
				a.download = `catalog-export-${new Date().toISOString().split("T")[0]}.json`;
				document.body.appendChild(a);
				a.click();
				document.body.removeChild(a);
				URL.revokeObjectURL(url);
			}

			return result;
		},
	});

	const importMut = useMutation({
		mutationFn: async () => {
			if (!importFile) return;
			const data = new Uint8Array(await importFile.arrayBuffer());
			return catalogClient.importCatalog({
				downloadUrl: "",
				overwrite,
				data,
				filename: importFile.name,
			});
		},
		onSuccess: () => {
			setImportFile(null);
			if (fileInputRef.current) fileInputRef.current.value = "";
		},
	});

	return (
		<div className="grid gap-6 lg:grid-cols-2">
			{/* Export */}
			<Card className="p-4">
				<div className="space-y-4">
					<div className="text-sm font-medium">
						{t("catalog_migration_export_title")}
					</div>
					<p className="text-xs text-muted-foreground">
						{t("catalog_migration_export_desc")}
					</p>

					<div className="space-y-2">
						<Label className="flex items-center gap-2 cursor-pointer">
							<Checkbox
								checked={includeEmbeddings}
								onCheckedChange={(c) => setIncludeEmbeddings(c === true)}
							/>
							{t("catalog_migration_include_embeddings")}
						</Label>
						<Label className="flex items-center gap-2 cursor-pointer">
							<Checkbox
								checked={includeEnrichment}
								onCheckedChange={(c) => setIncludeEnrichment(c === true)}
							/>
							{t("catalog_migration_include_enrichment")}
						</Label>
						<Label className="flex items-center gap-2 cursor-pointer">
							<Checkbox
								checked={includeCompliance}
								onCheckedChange={(c) => setIncludeCompliance(c === true)}
							/>
							{t("catalog_migration_include_compliance")}
						</Label>
					</div>

					{exportMut.data?.stats && (
						<p className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1.5">
							<CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
							{t("catalog_migration_export_stats", {
								categories: exportMut.data.stats.categories,
								products: exportMut.data.stats.products,
								embeddings: exportMut.data.stats.embeddings,
							})}
						</p>
					)}

					{exportMut.isError && (
						<p className="text-xs text-destructive flex items-center gap-1.5">
							<AlertCircle className="h-3.5 w-3.5" />
							{exportMut.error instanceof Error
								? exportMut.error.message
								: t("catalog_migration_export_error")}
						</p>
					)}

					<Button
						onClick={() => exportMut.mutate()}
						disabled={exportMut.isPending}
						variant="outline"
						size="sm"
						className="w-full"
					>
						{exportMut.isPending ? (
							<>
								<Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
								{t("catalog_migration_exporting")}
							</>
						) : (
							<>
								<Download className="h-3.5 w-3.5 mr-1.5" />
								{t("catalog_migration_export_button")}
							</>
						)}
					</Button>
				</div>
			</Card>

			{/* Import */}
			<Card className="p-4">
				<div className="space-y-4">
					<div className="text-sm font-medium">
						{t("catalog_migration_import_title")}
					</div>
					<p className="text-xs text-muted-foreground">
						{t("catalog_migration_import_desc")}
					</p>

					<div
						onClick={() => fileInputRef.current?.click()}
						onDragOver={(e) => {
							e.preventDefault();
							e.stopPropagation();
						}}
						onDrop={(e) => {
							e.preventDefault();
							e.stopPropagation();
							const file = Array.from(e.dataTransfer.files).find((f) =>
								f.name.toLowerCase().endsWith(".json"),
							);
							if (file) setImportFile(file);
						}}
						className={cn(
							"border-2 border-dashed rounded-lg p-5 text-center cursor-pointer transition-colors",
							"hover:border-primary/50 hover:bg-muted/50",
							importFile
								? "border-primary bg-primary/5"
								: "border-muted-foreground/25",
						)}
					>
						<input
							ref={fileInputRef}
							type="file"
							accept=".json,application/json"
							className="hidden"
							onChange={(e) => {
								const file = e.target.files?.[0];
								if (file) setImportFile(file);
							}}
						/>
						{importFile ? (
							<div className="flex items-center justify-center gap-2 text-sm">
								<Package className="h-4 w-4 text-primary" />
								<span className="font-medium truncate">{importFile.name}</span>
								<span className="text-xs text-muted-foreground">
									{t("catalog_migration_file_size_mb", {
										size: (importFile.size / 1024 / 1024).toFixed(1),
									})}
								</span>
								<Button
									variant="ghost"
									size="icon"
									className="h-6 w-6 text-muted-foreground hover:text-destructive"
									onClick={(e) => {
										e.stopPropagation();
										setImportFile(null);
									}}
								>
									<Trash2 className="h-3.5 w-3.5" />
								</Button>
							</div>
						) : (
							<div className="space-y-1">
								<Upload className="h-6 w-6 mx-auto text-muted-foreground" />
								<div className="text-sm text-muted-foreground">
									{t("catalog_migration_select_file")}
								</div>
							</div>
						)}
					</div>

					<div className="flex items-center justify-between">
						<span className="text-sm">{t("catalog_migration_overwrite")}</span>
						<Switch checked={overwrite} onCheckedChange={setOverwrite} />
					</div>

					{importMut.data?.stats && (
						<div className="rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-900 p-3 space-y-1.5 text-sm">
							<p className="font-medium text-green-700 dark:text-green-400 flex items-center gap-1.5">
								<CheckCircle2 className="h-3.5 w-3.5" />
								{t("catalog_migration_import_done")}
							</p>
							<div className="flex gap-4 text-xs">
								<span>
									{t("catalog_migration_import_categories")}{" "}
									<span className="text-green-600">
										+{importMut.data.stats.categoriesCreated}
									</span>
								</span>
								<span>
									{t("catalog_migration_import_products")}{" "}
									<span className="text-green-600">
										+{importMut.data.stats.productsCreated}
									</span>
								</span>
								<span>
									{t("catalog_migration_import_embeddings")}{" "}
									<span className="text-green-600">
										+{importMut.data.stats.embeddingsRestored}
									</span>
								</span>
							</div>
						</div>
					)}

					{importMut.isError && (
						<p className="text-xs text-destructive flex items-center gap-1.5">
							<AlertCircle className="h-3.5 w-3.5" />
							{importMut.error instanceof Error
								? importMut.error.message
								: t("catalog_migration_import_error")}
						</p>
					)}

					<Button
						onClick={() => importMut.mutate()}
						disabled={importMut.isPending || !importFile}
						variant="outline"
						size="sm"
						className="w-full"
					>
						{importMut.isPending ? (
							<>
								<Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
								{t("catalog_migration_importing")}
							</>
						) : (
							<>
								<Upload className="h-3.5 w-3.5 mr-1.5" />
								{t("catalog_migration_import_button")}
							</>
						)}
					</Button>
				</div>
			</Card>
		</div>
	);
}

// ── Synonyms Tab ─────────────────────────────

const PAGE_SIZE = 50;

export function SynonymsTab() {
	const { t } = useTranslation("settings");
	const qc = useQueryClient();
	const [showDialog, setShowDialog] = useState(false);
	const [editingSynonym, setEditingSynonym] = useState<SearchSynonym | null>(
		null,
	);
	const [searchQuery, setSearchQuery] = useState("");
	const [page, setPage] = useState(0);
	const [deleteTarget, setDeleteTarget] = useState<SearchSynonym | null>(null);
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [importResult, setImportResult] = useState<{
		imported: number;
		skipped: number;
		errors: string[];
	} | null>(null);

	const { data: synonymsResp, isLoading } = useQuery({
		queryKey: ["search-synonyms"],
		queryFn: () => catalogClient.listSearchSynonyms({}),
	});

	const allSynonyms = synonymsResp?.synonyms ?? [];

	// Client-side search filter
	const filtered = searchQuery.trim()
		? allSynonyms.filter((s) => {
				const q = searchQuery.toLowerCase();
				return (
					s.words.some((w) => w.toLowerCase().includes(q)) ||
					s.rootWord?.toLowerCase().includes(q)
				);
			})
		: allSynonyms;

	const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
	const currentPage = Math.min(page, totalPages - 1);
	const paged = filtered.slice(
		currentPage * PAGE_SIZE,
		(currentPage + 1) * PAGE_SIZE,
	);

	const deleteMut = useMutation({
		mutationFn: (id: string) => catalogClient.deleteSearchSynonym({ id }),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["search-synonyms"] });
			setDeleteTarget(null);
		},
	});

	const syncMut = useMutation({
		mutationFn: () => catalogClient.syncSearchSynonyms({}),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["search-synonyms"] });
		},
	});

	const importMut = useMutation({
		mutationFn: async (file: File) => {
			const buf = await file.arrayBuffer();
			return catalogClient.importSearchSynonyms({
				csvData: new Uint8Array(buf),
				replaceExisting: false,
			});
		},
		onSuccess: (result) => {
			qc.invalidateQueries({ queryKey: ["search-synonyms"] });
			setImportResult({
				imported: result.imported,
				skipped: result.skipped,
				errors: result.errors,
			});
		},
	});

	const deleteAllMut = useMutation({
		mutationFn: () =>
			catalogClient.importSearchSynonyms({
				csvData: new Uint8Array(),
				replaceExisting: true,
			}),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["search-synonyms"] });
			setPage(0);
		},
	});

	const [showDeleteAll, setShowDeleteAll] = useState(false);

	return (
		<div className="space-y-4">
			<div className="flex items-center justify-between">
				<div>
					<h3 className="text-sm font-medium">{t("catalog_synonyms_title")}</h3>
					<p className="text-xs text-muted-foreground mt-0.5">
						{t("catalog_synonyms_desc")}
					</p>
				</div>
				<div className="flex gap-2">
					{allSynonyms.length > 0 && (
						<Button
							variant="outline"
							size="sm"
							className="text-destructive hover:text-destructive"
							onClick={() => setShowDeleteAll(true)}
							disabled={deleteAllMut.isPending}
						>
							{deleteAllMut.isPending ? (
								<Loader2 className="h-3.5 w-3.5 animate-spin" />
							) : (
								<Trash2 className="h-3.5 w-3.5" />
							)}
						</Button>
					)}
					<Button
						variant="outline"
						size="sm"
						onClick={() => syncMut.mutate()}
						disabled={syncMut.isPending}
					>
						{syncMut.isPending ? (
							<Loader2 className="h-3.5 w-3.5 animate-spin" />
						) : (
							<RefreshCw className="h-3.5 w-3.5" />
						)}
					</Button>
					<Button
						variant="outline"
						size="sm"
						onClick={() => fileInputRef.current?.click()}
						disabled={importMut.isPending}
					>
						{importMut.isPending ? (
							<Loader2 className="h-3.5 w-3.5 animate-spin" />
						) : (
							<FileUp className="h-3.5 w-3.5" />
						)}
					</Button>
					<input
						ref={fileInputRef}
						type="file"
						accept=".csv,.tsv,text/csv"
						className="hidden"
						onChange={(e) => {
							const file = e.target.files?.[0];
							if (file) {
								setImportResult(null);
								importMut.mutate(file);
							}
							e.target.value = "";
						}}
					/>
					<Button
						size="sm"
						onClick={() => {
							setEditingSynonym(null);
							setShowDialog(true);
						}}
					>
						<Plus className="h-3.5 w-3.5 mr-1.5" />
						{t("common:add")}
					</Button>
				</div>
			</div>

			{/* Import result */}
			{importResult && (
				<div
					className={cn(
						"px-3 py-2 rounded-lg text-sm",
						importResult.errors.length > 0
							? "bg-amber-50 text-amber-800 dark:bg-amber-900/20 dark:text-amber-300"
							: "bg-green-50 text-green-800 dark:bg-green-900/20 dark:text-green-300",
					)}
				>
					<div className="flex items-center justify-between">
						<span>
							{t("catalog_synonyms_import_result", {
								imported: importResult.imported,
								skipped: importResult.skipped,
							})}
						</span>
						<Button
							variant="ghost"
							size="icon"
							onClick={() => setImportResult(null)}
							className="h-6 w-6 text-current opacity-60 hover:opacity-100"
						>
							<Trash2 className="h-3.5 w-3.5" />
						</Button>
					</div>
					{importResult.errors.length > 0 && (
						<div className="mt-1 text-xs opacity-80">
							{importResult.errors.slice(0, 3).map((err, i) => (
								<div key={i}>{err}</div>
							))}
							{importResult.errors.length > 3 && (
								<div>
									{t("catalog_synonyms_import_errors_more", {
										count: importResult.errors.length - 3,
									})}
								</div>
							)}
						</div>
					)}
				</div>
			)}

			{/* CSV format hint */}
			<p className="text-xs text-muted-foreground">
				CSV: <code className="bg-muted px-1 rounded">words</code> +{" "}
				<code className="bg-muted px-1 rounded">root_word</code>{" "}
				{t("catalog_synonyms_csv_hint_or")}{" "}
				<code className="bg-muted px-1 rounded">word</code> +{" "}
				<code className="bg-muted px-1 rounded">synonym</code>
			</p>

			{/* Search */}
			<div className="relative">
				<Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
				<Input
					placeholder={t("catalog_synonyms_search_placeholder")}
					value={searchQuery}
					onChange={(e) => {
						setSearchQuery(e.target.value);
						setPage(0);
					}}
					className="pl-8 h-8 text-sm"
				/>
			</div>

			{isLoading ? (
				<div className="flex items-center justify-center py-8">
					<Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
				</div>
			) : filtered.length === 0 ? (
				<p className="text-sm text-muted-foreground py-4 text-center">
					{searchQuery
						? t("common:no_results")
						: t("catalog_synonyms_no_synonyms")}
				</p>
			) : (
				<>
					<div className="rounded-lg border overflow-hidden">
						<Table>
							<TableHeader>
								<TableRow className="border-b bg-muted/30">
									<TableHead className="px-3 py-2 text-left font-medium text-muted-foreground w-28">
										{t("catalog_synonyms_col_type")}
									</TableHead>
									<TableHead className="px-3 py-2 text-left font-medium text-muted-foreground">
										{t("catalog_synonyms_col_root")}
									</TableHead>
									<TableHead className="px-3 py-2 text-left font-medium text-muted-foreground">
										{t("catalog_synonyms_col_words")}
									</TableHead>
									<TableHead className="px-3 py-2 text-right font-medium text-muted-foreground w-20" />
								</TableRow>
							</TableHeader>
							<TableBody className="divide-y">
								{paged.map((syn) => (
									<TableRow key={syn.id} className="hover:bg-muted/50">
										<TableCell className="px-3 py-2">
											<span
												className={cn(
													"text-xs px-1.5 py-0.5 rounded",
													syn.synonymType === "one_way"
														? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
														: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
												)}
											>
												{syn.synonymType === "one_way" ? "→" : "↔"}
											</span>
											{!syn.isActive && (
												<span className="ml-1.5 text-xs text-muted-foreground">
													{t("catalog_synonyms_disabled")}
												</span>
											)}
										</TableCell>
										<TableCell className="px-3 py-2 font-medium">
											{syn.rootWord || "—"}
										</TableCell>
										<TableCell className="px-3 py-2 text-muted-foreground">
											{syn.words.join(", ")}
										</TableCell>
										<TableCell className="px-3 py-2 text-right">
											<div className="flex items-center justify-end gap-1">
												<Button
													variant="ghost"
													size="sm"
													className="h-7 w-7 p-0"
													onClick={() => {
														setEditingSynonym(syn);
														setShowDialog(true);
													}}
												>
													<Settings className="h-3.5 w-3.5" />
												</Button>
												<Button
													variant="ghost"
													size="sm"
													className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
													onClick={() => setDeleteTarget(syn)}
												>
													<Trash2 className="h-3.5 w-3.5" />
												</Button>
											</div>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</div>

					{/* Pagination */}
					<div className="flex items-center justify-between text-sm text-muted-foreground">
						<span>
							{t("catalog_synonyms_count", {
								count: formatNumber(filtered.length),
							})}
							{searchQuery &&
								` ${t("catalog_synonyms_count_filtered", { total: formatNumber(allSynonyms.length) })}`}
						</span>
						{totalPages > 1 && (
							<div className="flex items-center gap-2">
								<Button
									variant="outline"
									size="sm"
									disabled={currentPage === 0}
									onClick={() => setPage((p) => p - 1)}
								>
									{t("common:back")}
								</Button>
								<span className="tabular-nums">
									{currentPage + 1} / {totalPages}
								</span>
								<Button
									variant="outline"
									size="sm"
									disabled={currentPage >= totalPages - 1}
									onClick={() => setPage((p) => p + 1)}
								>
									{t("common:next")}
								</Button>
							</div>
						)}
					</div>
				</>
			)}

			<SynonymDialog
				open={showDialog}
				onOpenChange={setShowDialog}
				synonym={editingSynonym}
			/>

			{/* Delete single confirmation */}
			<AlertDialog
				open={!!deleteTarget}
				onOpenChange={(open) => !open && setDeleteTarget(null)}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							{t("catalog_synonyms_delete_title")}
						</AlertDialogTitle>
						<AlertDialogDescription>
							{t("catalog_synonyms_delete_desc")}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>{t("common:cancel")}</AlertDialogCancel>
						<AlertDialogAction
							onClick={() => deleteTarget && deleteMut.mutate(deleteTarget.id)}
							className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
						>
							{t("common:delete")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			{/* Delete all confirmation */}
			<AlertDialog open={showDeleteAll} onOpenChange={setShowDeleteAll}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							{t("catalog_synonyms_delete_all_title")}
						</AlertDialogTitle>
						<AlertDialogDescription>
							{t("catalog_synonyms_delete_all_desc", {
								count: formatNumber(allSynonyms.length),
							})}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>{t("common:cancel")}</AlertDialogCancel>
						<AlertDialogAction
							onClick={() => {
								deleteAllMut.mutate();
								setShowDeleteAll(false);
							}}
							className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
						>
							{t("catalog_synonyms_delete_all_button")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
}

function SynonymDialog({
	open,
	onOpenChange,
	synonym,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	synonym: SearchSynonym | null;
}) {
	const { t } = useTranslation("settings");
	const qc = useQueryClient();
	const [synonymType, setSynonymType] = useState(
		synonym?.synonymType || "multi_way",
	);
	const [rootWord, setRootWord] = useState(synonym?.rootWord || "");
	const [wordsInput, setWordsInput] = useState(synonym?.words.join(", ") || "");
	const [isActive, setIsActive] = useState(synonym?.isActive ?? true);

	// Reset form state when dialog opens or synonym changes
	useEffect(() => {
		if (open) {
			setSynonymType(synonym?.synonymType || "multi_way");
			setRootWord(synonym?.rootWord || "");
			setWordsInput(synonym?.words.join(", ") || "");
			setIsActive(synonym?.isActive ?? true);
		}
	}, [synonym?.id, open]);

	const isEditing = !!synonym;

	const createMut = useMutation({
		mutationFn: () =>
			catalogClient.createSearchSynonym({
				synonymType,
				rootWord: synonymType === "one_way" ? rootWord : "",
				words: wordsInput
					.split(",")
					.map((w) => w.trim())
					.filter(Boolean),
			}),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["search-synonyms"] });
			onOpenChange(false);
		},
	});

	const updateMut = useMutation({
		mutationFn: () =>
			catalogClient.updateSearchSynonym({
				id: synonym!.id,
				synonymType,
				rootWord: synonymType === "one_way" ? rootWord : "",
				words: wordsInput
					.split(",")
					.map((w) => w.trim())
					.filter(Boolean),
				isActive,
			}),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["search-synonyms"] });
			onOpenChange(false);
		},
	});

	const handleSubmit = () => {
		if (isEditing) {
			updateMut.mutate();
		} else {
			createMut.mutate();
		}
	};

	const isPending = createMut.isPending || updateMut.isPending;

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>
						{isEditing
							? t("catalog_synonyms_dialog_title_edit")
							: t("catalog_synonyms_dialog_title_create")}
					</DialogTitle>
				</DialogHeader>

				<div className="space-y-4 py-4">
					<div className="space-y-2">
						<Label>{t("catalog_synonyms_type_label")}</Label>
						<Select value={synonymType} onValueChange={setSynonymType}>
							<SelectTrigger>
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="multi_way">
									{t("catalog_synonyms_type_multi_way")}
								</SelectItem>
								<SelectItem value="one_way">
									{t("catalog_synonyms_type_one_way")}
								</SelectItem>
							</SelectContent>
						</Select>
					</div>

					{synonymType === "one_way" && (
						<div className="space-y-2">
							<Label>{t("catalog_synonyms_root_word")}</Label>
							<Input
								value={rootWord}
								onChange={(e) => setRootWord(e.target.value)}
								placeholder={t("catalog_synonyms_root_placeholder")}
							/>
						</div>
					)}

					<div className="space-y-2">
						<Label>
							{synonymType === "one_way"
								? t("catalog_synonyms_words_label_one_way")
								: t("catalog_synonyms_words_label_multi_way")}
						</Label>
						<Input
							value={wordsInput}
							onChange={(e) => setWordsInput(e.target.value)}
							placeholder={t("catalog_synonyms_words_placeholder")}
						/>
						<p className="text-xs text-muted-foreground">
							{t("catalog_synonyms_words_hint")}
						</p>
					</div>

					{isEditing && (
						<div className="flex items-center justify-between">
							<Label>{t("catalog_synonyms_active")}</Label>
							<Switch checked={isActive} onCheckedChange={setIsActive} />
						</div>
					)}
				</div>

				<DialogFooter>
					<Button variant="outline" onClick={() => onOpenChange(false)}>
						{t("common:cancel")}
					</Button>
					<Button onClick={handleSubmit} disabled={isPending}>
						{isPending && (
							<Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
						)}
						{isEditing ? t("common:save") : t("common:create")}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
